import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage'
import { nanoid } from 'nanoid'
import imageCompression from 'browser-image-compression'
import FirebaseApp from './firebase'

const storage = getStorage(FirebaseApp)

const compressImage = async (file) => {
  const options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  }
  try {
    return await imageCompression(file, options)
  } catch (error) {
    console.error('Ошибка сжатия изображения:', error)
    return file // Возвращаем оригинальный файл в случае ошибки
  }
}

/**
 * Загружает изображение для организации (например, логотип или аватар).
 * @param {string} organizationId - ID организации.
 * @param {File} file - Файл для загрузки.
 * @returns {Promise<string>} URL загруженного файла.
 */
export const uploadOrganizationImage = async (organizationId, file) => {
  const compressedFile = await compressImage(file)

  // Генерируем безопасное, уникальное имя файла, сохраняя расширение.
  let fileExtension
  if (compressedFile.name) {
    fileExtension = compressedFile.name.split('.').pop()
  } else {
    fileExtension = 'jpg'
  }
  const uniqueFileName = `${nanoid()}.${fileExtension}`

  const filePath = `organizations/${organizationId}/images/${uniqueFileName}`
  const fileRef = ref(storage, filePath)
  await uploadBytes(fileRef, compressedFile)
  return getDownloadURL(fileRef)
}

/**
 * Загружает изображение для услуги.
 * @param {string} organizationId - ID организации.
 * @param {string} serviceId - ID услуги.
 * @param {File} file - Файл для загрузки.
 * @returns {Promise<string>} URL загруженного файла.
 */
export const uploadServiceImage = async (organizationId, serviceId, file) => {
  const compressedFile = await compressImage(file)

  // Генерируем безопасное, уникальное имя файла, сохраняя расширение.
  const fileExtension = compressedFile.name.split('.').pop()
  const uniqueFileName = `${nanoid()}.${fileExtension}`

  const filePath = `organizations/${organizationId}/services/${serviceId}/images/${uniqueFileName}`
  const fileRef = ref(storage, filePath)
  await uploadBytes(fileRef, compressedFile)
  return getDownloadURL(fileRef)
}

/**
 * Uploads an avatar for a client to Firebase Storage.
 * @param {string} orgId - The organization ID.
 * @param {string} clientId - The client ID.
 * @param {File} file - The avatar file to upload.
 * @returns {Promise<string>} The download URL of the uploaded avatar.
 */
export const uploadClientAvatar = async (orgId, clientId, file) => {
  if (!orgId || !clientId || !file) {
    throw new Error('Missing parameters for avatar upload.')
  }
  const compressedFile = await compressImage(file)
  let fileExtension
  if (compressedFile.name) {
    fileExtension = compressedFile.name.split('.').pop()
  } else {
    fileExtension = 'jpg'
  }
  const filePath = `organizations/${orgId}/clients/${clientId}/avatar.${fileExtension}`
  const storageRef = ref(storage, filePath)
  await uploadBytes(storageRef, compressedFile)
  return getDownloadURL(storageRef)
}

/**
 * Deletes an image from Firebase Storage using its full URL.
 * @param {string} imageUrl The full HTTPS URL of the image.
 * @returns {Promise<void>}
 */
export const deleteImageByUrl = async (imageUrl) => {
  if (!imageUrl) {
    return
  }

  try {
    // Create a reference from the HTTPS URL
    const imageRef = ref(storage, imageUrl)
    await deleteObject(imageRef)
  } catch (error) {
    // It's common for this to fail if the URL is malformed, the object doesn't exist, or there are permission issues.
    // We log a warning instead of throwing an error to avoid breaking the UI flow.
    console.warn(`Failed to delete image from storage: ${imageUrl}`, error)
  }
}
